import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthService } from 'src/app/auth/auth.service';
import {
  CreateHealthMemberPayload,
  HealthMemberFilterParams,
  MembroEquipeSaude,
  UpdateHealthMemberPayload,
} from 'src/app/models/membro-equipe-saude.model';
import { captureError, logErrorAction, logInfoAction } from "src/app/shared/util/error-capture.util";
import { environment } from 'src/environments/environment';
import { InstitutionChangeResponse, InstitutionResponse } from '../models/unidade-de-saude.model';
import { ImportService } from '../shared/interfaces/import.interface';

@Injectable({
  providedIn: 'root',
})
export class HealthMemberService implements ImportService {
  API = `${environment.backendUrl}membroEquipeSaude`;
  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) { }

  async import(file): Promise<Response> {
    try {
      const headers = await this.authService.buildAPIAuthHeader(file.type);
      const uploadedFile = new FormData();
      uploadedFile.append('file', file);
      logInfoAction('Health member import started', { fileName: file?.name, fileType: file?.type });
      return await this.http.post<Response>(this.API + '/importar', uploadedFile, { headers }).toPromise();
    } catch (error) {
      logErrorAction('Health member import failed', error);
      captureError(error, {
        component: 'health-member-service',
        operation: 'import'
      });
      throw error;
    }
  }

  async getHealthMemberInfo(): Promise<MembroEquipeSaude> {
    try {
      const headers = await this.authService.buildAPIAuthHeader();
      logInfoAction('Health member info fetch started', { userId: this.authService.getUserData().uid });
      return this.http
        .get<MembroEquipeSaude>(
          this.API + `/usuario/${this.authService.getUserData().uid}`,
          {
            headers,
          }
        )
        .toPromise();
    } catch (error: any) {
      logErrorAction('Health member info fetch failed', error);
      captureError(error, {
        component: 'health-member-service',
        operation: 'loadHealthMembersInfo'
      });
      throw error;
    }
  }

  async registerHealthMember(payload: CreateHealthMemberPayload): Promise<any> {
    try {
      logInfoAction('Health member registration started', { payload });
      const headers = await this.authService.buildAPIAuthHeader();
      return this.http.post(this.API, payload, { headers }).toPromise();
    } catch (error: any) {
      logErrorAction('Health member registration failed', error);
      captureError(error, {
        component: 'health-member-service',
        operation: 'registerHealthMember'
      });
      throw error;
    }
  }

  async getHealthMember(healthMemberId: string): Promise<MembroEquipeSaude> {
    try {
      const headers = await this.authService.buildAPIAuthHeader();
      logInfoAction('Health member fetch started', { healthMemberId });
      return this.http
        .get<MembroEquipeSaude>(`${this.API}/${healthMemberId}`, { headers })
        .toPromise();
    } catch (error) {
      logErrorAction('Health member fetch failed', error);
      captureError(error, {
        component: 'health-member-service',
        operation: 'getHealthMember'
      });
      throw error;
    }
  }

  async loadHealthMembers(
    queryParams?: HealthMemberFilterParams
  ): Promise<{ count: number; healthMembers: Array<MembroEquipeSaude> }> {
    try {
      const headers = await this.authService.buildAPIAuthHeader();
      const options = { headers, params: {} };

      if (queryParams) {
        let params = new HttpParams();

        if (queryParams.filterName != null && queryParams.filterName.length) {
          params = params.set('filterName', queryParams.filterName);
        }

        if (queryParams.filterCPF != null && queryParams.filterCPF.length) {
          params = params.set(
            'filterCPF',
            queryParams.filterCPF.replace(/\.|-/g, '')
          );
        }

        if (queryParams.filterRG != null && queryParams.filterRG.length) {
          params = params.set(
            'filterRG',
            queryParams.filterRG.replace(/\.|-/g, '')
          );
        }

        if (queryParams.filterProfile != null && queryParams.filterProfile.length) {
          params = params.set('filterProfile', queryParams.filterProfile);
        }

        if (queryParams.page != null) {
          params = params.set('page', queryParams.page);
        }

        if (queryParams.pageSize != null) {
          params = params.set('pageSize', queryParams.pageSize + 1);
        }

        options.params = params;
      }
      logInfoAction('Health members loading started', { queryParams });
      return this.http
        .get<{ count: number; healthMembers: Array<MembroEquipeSaude> }>(
          this.API,
          options
        )
        .toPromise();
    } catch (error) {
      logErrorAction('Health members loading failed', error);
      captureError(error, {
        component: 'health-member-service',
        operation: 'loadHealthMembersInfo'
      });
      throw error;
    }
  }

  async updateHealthMember(
    healthMemberId: string | null,
    updateHealthMember: UpdateHealthMemberPayload
  ): Promise<void> {
    try {
      const headers = await this.authService.buildAPIAuthHeader();
      logInfoAction('Health member update started', { healthMemberId, payload: updateHealthMember });
      this.http
        .patch(`${this.API}/${healthMemberId}`, updateHealthMember, { headers })
        .toPromise();
    } catch (error) {
      logErrorAction('Health member update failed', error);
      captureError(error, {
        component: 'health-member-service',
        operation: 'updateHealthMember'
      });
      throw error;
    }
  }

  async getHealthMemberInstitutionList(): Promise<InstitutionResponse> {
    try {
      const headers = await this.authService.buildAPIAuthHeader();
      logInfoAction('Health member institution list fetch started', {});
      const response = await this.http
        .get<InstitutionResponse>(`${this.API}/minhas-instituicoes`, { headers })
        .toPromise();
      return response;
    } catch (error: any) {
      logErrorAction('Health member institution list fetch failed', error);
      captureError(error, {
        component: 'health-member-service',
        operation: 'getHealthMemberInstitutionList',
        extraData: {
          error: error || 'Unknown error'
        }
      });
      throw error;
    }
  }

  async changeHealthMemberActiveInstitution(
    institutionId: number
  ): Promise<InstitutionChangeResponse> {
    try {
      logInfoAction('Health member active institution change started', { institutionId });
      const headers = await this.authService.buildAPIAuthHeader();
      const resp = await this.http
        .post<InstitutionChangeResponse>(`${this.API}/trocar-instituicao-ativa`, { institutionId }, { headers })
        .toPromise();
      return resp;
    } catch (error) {
      logErrorAction('Health member active institution change failed', error);
      captureError(error, {
        component: 'health-member-service',
        operation: 'changeHealthMemberActiveInstitution',
        extraData: {
          error: error || 'Unknown error'
        }
      });
      throw error;
    }
  }
}
